<script setup lang="ts">
  import { reactive, computed } from "vue";
  import { Modal, Text, Button, Checkbox } from "@ui-kit";
  import { useUserInfoStore, useOrderListFiltersStore } from "@src/store";
  import { TextService } from "@src/services";

  const emit = defineEmits<{ (e: "close"): void }>();

  const user_info_store = useUserInfoStore();
  const order_list_filters_store = useOrderListFiltersStore();

  const state = reactive<{ section: string[]; store: string[] }>({
    section: [...order_list_filters_store.section.value],
    store: [...order_list_filters_store.store.value],
  });

  const section_options = computed(
    () => user_info_store.shop?.sections.map((section) => ({ value: section, label: section })) ?? [],
  );
  const store_options = computed(() =>
    user_info_store.store_types.map((store) => ({ value: store, label: TextService.capitalize(store) })),
  );
  const button_apply_disabled = computed(
    () =>
      JSON.stringify({ section: [...state.section].sort(), store: [...state.store].sort() }) ===
      JSON.stringify({
        section: [...order_list_filters_store.section.value].sort(),
        store: [...order_list_filters_store.store.value].sort(),
      }),
  );

  const toggleOption = (filter_name: "section" | "store", optionValue: string, checked: boolean) => {
    if (checked) {
      if (!state[filter_name].includes(optionValue)) {
        state[filter_name].push(optionValue);
      }
    } else {
      state[filter_name] = state[filter_name].filter((value) => value !== optionValue);
    }
  };

  const handleApply = () => {
    order_list_filters_store.update_section([...state.section]);
    order_list_filters_store.update_store([...state.store]);
    emit("close");
  };

  const handleReset = () => {
    order_list_filters_store.update_section([]);
    order_list_filters_store.update_store([]);
    emit("close");
  };
</script>

<template>
  <Modal grabber close :border="false" @on-close="emit('close')" data-testid="filter-modal">
    <template #header>
      <Text font="body-bold-18">{{ $t("Фильтры") }}</Text>
    </template>
    <div class="filter-modal">
      <div v-if="section_options.length > 0" class="filter-modal__section">
        <Text font="body-medium-14">{{ $t("Секция") }}</Text>
        <div class="filter-modal__section__option-list">
          <Checkbox
            v-for="option in section_options"
            :key="option.value"
            :label="option.label"
            :model-value="state.section.includes(option.value)"
            @update:model-value="(checked: boolean) => toggleOption('section', option.value, checked)"
            :data-testid="`section-checkbox-${option.value.toLowerCase()}`"
          />
        </div>
      </div>
      <div v-if="store_options.length > 0" class="filter-modal__section">
        <Text font="body-medium-14">{{ $t("Склад") }}</Text>
        <div class="filter-modal__section__option-list">
          <Checkbox
            v-for="option in store_options"
            :key="option.value"
            :label="option.label"
            :model-value="state.store.includes(option.value)"
            @update:model-value="(checked: boolean) => toggleOption('store', option.value, checked)"
            :data-testid="`store-checkbox-${option.value.toLowerCase()}`"
          />
        </div>
      </div>
    </div>
    <template #footer>
      <div class="filter-modal__footer">
        <Button variant="secondary" @click="handleReset" data-testid="filter-reset">
          {{ $t("Сбросить") }}
        </Button>
        <Button :disabled="button_apply_disabled" @click="handleApply" data-testid="filter-apply">
          {{ $t("Применить") }}
        </Button>
      </div>
    </template>
  </Modal>
</template>

<style scoped>
  .filter-modal {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .filter-modal__section {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .filter-modal__section__option-list {
    display: flex;
    flex-direction: column;

    & :deep(.checkbox) {
      padding: 12px 0;
    }
  }

  .filter-modal__footer {
    display: flex;
    gap: 8px;

    & :deep(.button) {
      flex: 1;
    }
  }
</style>
